/**
 * The language switcher.
 *
 * Three links, the current one marked, rendered twice: in the header beside the
 * sign-in button, and inside the mobile menu. One component rather than two copies
 * because a switcher that lists two languages on a phone is the single most common
 * way a translated site ends up half-translated.
 *
 * ## It changes the address, not a setting
 *
 * Each option links to the same route in that language, so switching keeps the reader
 * on the page they were looking at. That is why it is a `Link` and not a button with
 * a click handler: the browser gets a real `href`, so the option can be opened in a
 * new tab, copied, and crawled, and the page it points at is one that renders on its
 * own without this app having run.
 *
 * ## The label is the language's own name
 *
 * "English", not "Inglês". A reader who does not read the current language has no way
 * to guess that "Inglês" means English, and the full word is what a screen reader
 * should say. The two-letter form is what is painted, because three full words do not
 * fit beside a sign-in button at 640px.
 */

import React from 'react';

import { LOCALES, localeLabel, localeShortLabel, useI18n } from '../i18n';
import { Link, useRouter } from '../router';

export const LanguageSwitcher: React.FC<{ className?: string }> = ({ className }) => {
  const { locale, route } = useRouter();
  const { t } = useI18n();

  return (
    <nav aria-label={t.chrome.languageLabel} className={className}>
      {LOCALES.map((candidate) => {
        const active = candidate === locale;
        return (
          <Link
            key={candidate}
            to={route}
            locale={candidate}
            // `aria-current="true"` rather than `page`: this is not a page, it is a
            // representation of the same page. A screen reader announcing "current
            // page" next to a link that goes somewhere else is a small lie.
            aria-current={active ? 'true' : undefined}
            className={`rounded-[6px] px-1.5 py-1 text-[12px] font-medium transition-colors ${
              active
                ? 'text-cream'
                : 'text-muted hover:text-cream'
            }`}
          >
            <span className="sr-only">{localeLabel(candidate)}</span>
            <span aria-hidden="true">{localeShortLabel(candidate)}</span>
          </Link>
        );
      })}
    </nav>
  );
};